import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import useTracking from '@/hooks/useTracking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ArrowLeft, Trash2, Plus, Minus, ShoppingBag, CreditCard, Truck, Loader2, CheckCircle, QrCode, Wallet, Building, Tag, Zap, Package, TrendingUp, X, Gift, Landmark, Upload, Copy, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function Checkout() {
  const subdomain = useStoreSubdomain();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [orderResult, setOrderResult] = useState(null);
  const [codSettings, setCodSettings] = useState(null);

  // Voucher state
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [discount, setDiscount] = useState(0);

  // Loyalty points state
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loyaltyPointsInput, setLoyaltyPointsInput] = useState('');
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loyaltyPointsUsed, setLoyaltyPointsUsed] = useState(0);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);

  // Order Bumps state
  const [orderBumps, setOrderBumps] = useState([]);
  const [selectedBumps, setSelectedBumps] = useState([]);
  const [bundleSuggestions, setBundleSuggestions] = useState([]);

  // Upsell/Downsell state
  const [upsellOffers, setUpsellOffers] = useState([]);
  const [upsellState, setUpsellState] = useState('idle'); // idle | showing_upsell | showing_downsell | done
  const [currentFunnel, setCurrentFunnel] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [upsellOrderResult, setUpsellOrderResult] = useState(null);

  // Bank transfer state
  const [proofForm, setProofForm] = useState({ bank_name: '', account_name: '', amount: '', transfer_date: '', proof_url: '', notes: '' });
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const [proofSubmitted, setProofSubmitted] = useState(false);

  // Shipping state
  const [shippingInfo, setShippingInfo] = useState(null);
  const [shippingCosts, setShippingCosts] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [destSearch, setDestSearch] = useState('');
  const [destResults, setDestResults] = useState([]);
  const [destSearching, setDestSearching] = useState(false);
  const [selectedDest, setSelectedDest] = useState(null);
  const [selectedCourier, setSelectedCourier] = useState('');

  const [form, setForm] = useState(() => {
    // Auto-fill from logged-in customer data
    const saved = localStorage.getItem('user');
    let init = { customer_name: '', customer_email: '', customer_phone: '', customer_address: '', payment_method: 'qris', is_cod: false, notes: '', voucher_code: '', affiliate_code: '', reseller_code: '', is_dropship: false, dropship_sender_name: '', dropship_sender_phone: '' };
    if (saved) {
      try {
        const u = JSON.parse(saved);
        if (u.role === 'customer') { init.customer_name = u.name || ''; init.customer_email = u.email || ''; }
      } catch {}
    }
    return init;
  });

  // Affiliate validation state
  const [affiliateName, setAffiliateName] = useState('');
  const [affiliateValid, setAffiliateValid] = useState(null);

  // Reseller state
  const [resellerPrices, setResellerPrices] = useState(null);
  const [resellerTierName, setResellerTierName] = useState('');
  const { trackEvent } = useTracking(store?.store_extras);

  const hasPhysicalItems = cart.some(c => c.type === 'physical') || cart.some(c => !c.type);

  // Load store, cart, shipping info, order bumps
  useEffect(() => {
    const fetchData = async () => {
      try {
        const storeRes = await api.get(`/store/${subdomain}`);
        setStore(storeRes.data);

        // Load shipping info
        try {
          const shipRes = await api.get(`/store/${subdomain}/shipping-info`);
          setShippingInfo(shipRes.data);
        } catch {}

        // Load COD settings
        try {
          const codRes = await api.get(`/store/${subdomain}/cod-settings`);
          setCodSettings(codRes.data);
        } catch {}
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Pre-fill phone & address from customer profile if logged in
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        if (u.role === 'customer' && localStorage.getItem('token')) {
          api.get('/customer/profile').then(res => {
            const c = res.data.customer;
            if (c) setForm(f => ({ ...f, customer_phone: f.customer_phone || c.phone || '', customer_address: f.customer_address || c.address || '' }));
          }).catch(() => {});
          api.get('/customer/addresses').then(res => {
            const def = (res.data.addresses || []).find(a => a.is_default);
            if (def) setForm(f => ({ ...f, customer_address: f.customer_address || [def.address, def.city, def.province].filter(Boolean).join(', '), customer_phone: f.customer_phone || def.phone || '' }));
          }).catch(() => {});
        }
      } catch {}
    }

    // Load cart from localStorage
    const savedCart = localStorage.getItem(`cart_${subdomain}`);
    if (savedCart) setCart(JSON.parse(savedCart));

    // Try to load persistent cart if logged in
    const token = localStorage.getItem('token');
    if (token && !savedCart) {
      api.get(`/store/${subdomain}/cart`).then(res => {
        if (res.data.items?.length > 0) {
          setCart(res.data.items);
          localStorage.setItem(`cart_${subdomain}`, JSON.stringify(res.data.items));
        }
      }).catch(() => {});
    }

    // Get affiliate code from URL or saved referral
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || localStorage.getItem(`affiliate_ref_${subdomain}`) || '';
    if (ref) {
      setForm(f => ({ ...f, affiliate_code: ref }));
      // Validate affiliate code
      api.get(`/store/${subdomain}/validate-affiliate`, { params: { code: ref } })
        .then(res => {
          if (res.data.valid) {
            setAffiliateName(res.data.name);
            setAffiliateValid(true);
          } else {
            setAffiliateValid(false);
          }
        })
        .catch(() => setAffiliateValid(false));
    }
  }, [subdomain]);

  // Fire InitiateCheckout when store + cart ready
  useEffect(() => {
    if (store && cart.length > 0) {
      trackEvent('InitiateCheckout', { value: cart.reduce((a, c) => a + c.price * c.quantity, 0), num_items: cart.length });
    }
  }, [store, cart.length, trackEvent]);

  // Fetch order bumps and bundle suggestions when cart changes
  useEffect(() => {
    if (cart.length === 0) {
      setOrderBumps([]);
      setBundleSuggestions([]);
      return;
    }
    const productIds = cart.filter(c => !c.is_bundle).map(c => c.product_id).join(',');
    if (productIds) {
      api.get(`/store/${subdomain}/order-bumps?product_ids=${productIds}`).then(res => {
        setOrderBumps(res.data.order_bumps || []);
      }).catch(() => {});
      api.get(`/store/${subdomain}/bundle-suggestions?product_ids=${productIds}`).then(res => {
        setBundleSuggestions(res.data.bundles || []);
      }).catch(() => {});
    } else {
      setOrderBumps([]);
      setBundleSuggestions([]);
    }
  }, [cart, subdomain]);

  // Save cart to persistent storage (debounced)
  const saveCartPersistent = useCallback((items) => {
    const token = localStorage.getItem('token');
    if (token && subdomain) {
      api.post('/store/cart/save', {
        tenant_subdomain: subdomain,
        items: items.filter(c => !c.is_bundle).map(c => ({ product_id: c.product_id, quantity: c.quantity }))
      }).catch(() => {});
    }
  }, [subdomain]);

  const updateCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem(`cart_${subdomain}`, JSON.stringify(newCart));
    saveCartPersistent(newCart);
  };

  const updateQuantity = (idx, delta) => {
    const newCart = cart.map((c, i) => {
      if (i === idx) {
        const newQty = Math.max(0, c.quantity + delta);
        return newQty === 0 ? null : { ...c, quantity: newQty };
      }
      return c;
    }).filter(Boolean);
    updateCart(newCart);
  };

  const removeItem = (idx) => {
    const newCart = cart.filter((_, i) => i !== idx);
    updateCart(newCart);
  };

  // Add bundle to cart from suggestions
  const addBundleToCart = (bundle) => {
    const existing = cart.find(c => c.bundle_id === bundle.id);
    if (existing) {
      toast.info('Bundle sudah ada di keranjang');
      return;
    }
    const newCart = [...cart, {
      bundle_id: bundle.id,
      name: bundle.name,
      price: bundle.final_price,
      quantity: 1,
      image: bundle.images?.[0] || '',
      is_bundle: true,
      items: bundle.items || []
    }];
    updateCart(newCart);
    toast.success('Bundle ditambahkan ke keranjang');
  };

  // Toggle order bump
  const toggleBump = (bump) => {
    const isSelected = selectedBumps.find(b => b.id === bump.id);
    if (isSelected) {
      setSelectedBumps(selectedBumps.filter(b => b.id !== bump.id));
    } else {
      setSelectedBumps([...selectedBumps, bump]);
    }
  };

  // Validate voucher
  const validateVoucher = async () => {
    if (!voucherInput.trim()) return;
    setVoucherLoading(true);
    try {
      const res = await api.post(`/store/${subdomain}/validate-voucher?code=${encodeURIComponent(voucherInput.trim())}`);
      if (res.data.valid) {
        setVoucherApplied(res.data.voucher);
        setForm(f => ({ ...f, voucher_code: voucherInput.trim() }));
        // Calculate discount
        const v = res.data.voucher;
        let disc = 0;
        if (v.discount_type === 'percentage') {
          disc = Math.round(subtotal * v.discount_value / 100);
          if (v.max_discount > 0) disc = Math.min(disc, v.max_discount);
        } else {
          disc = Math.min(v.discount_value, subtotal);
        }
        if (v.min_purchase > 0 && subtotal < v.min_purchase) {
          toast.error(`Minimum pembelian Rp ${fmt(v.min_purchase)}`);
          return;
        }
        setDiscount(disc);
        toast.success(`Voucher "${v.code}" berhasil diterapkan! Diskon Rp ${fmt(disc)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kode voucher tidak valid');
      setVoucherApplied(null);
      setDiscount(0);
    } finally {
      setVoucherLoading(false);
    }
  };

  const removeVoucher = () => {
    setVoucherApplied(null);
    setVoucherInput('');
    setDiscount(0);
    setForm(f => ({ ...f, voucher_code: '' }));
  };

  // Loyalty points lookup
  const fetchLoyaltyInfo = async () => {
    if (!form.customer_email) return;
    try {
      const r = await api.get(`/store/${subdomain}/loyalty-info?email=${encodeURIComponent(form.customer_email)}`);
      if (r.data.enabled) {
        setLoyaltyInfo(r.data);
      }
    } catch { }
  };

  const applyLoyaltyPoints = async () => {
    const pts = parseInt(loyaltyPointsInput) || 0;
    if (pts <= 0) { toast.error('Masukkan jumlah poin'); return; }
    setLoyaltyLoading(true);
    try {
      const r = await api.post(`/store/${subdomain}/loyalty-validate?email=${encodeURIComponent(form.customer_email)}&points_to_redeem=${pts}&order_total=${subtotal - discount}`);
      if (r.data.valid) {
        setLoyaltyPointsUsed(r.data.points_to_redeem);
        setLoyaltyDiscount(r.data.discount);
        toast.success(`${r.data.points_to_redeem} poin diterapkan! Diskon Rp ${fmt(r.data.discount)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal validasi poin');
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const removeLoyaltyPoints = () => {
    setLoyaltyPointsUsed(0);
    setLoyaltyDiscount(0);
    setLoyaltyPointsInput('');
  };

  // Search destination — unified endpoint (same Komerce destination DB for all providers)
  const searchDestination = async () => {
    if (!destSearch || destSearch.length < 2) { toast.error('Ketik minimal 2 karakter'); return; }
    setDestSearching(true);
    try {
      const res = await api.get(`/store/shipping/destination-search?search=${encodeURIComponent(destSearch)}&limit=10`);
      const data = res.data?.data || res.data?.rajaongkir?.results || [];
      setDestResults(Array.isArray(data) ? data : []);
      if ((!data || data.length === 0)) toast.info('Tidak ditemukan lokasi');
    } catch { toast.error('Gagal mencari lokasi'); }
    finally { setDestSearching(false); }
  };

  // Calculate shipping after destination + courier selected
  const calculateShipping = async () => {
    if (!selectedDest) { toast.error('Pilih tujuan pengiriman'); return; }
    if (!selectedCourier) { toast.error('Pilih kurir'); return; }
    setShippingLoading(true);
    try {
      const destId = selectedDest.subdistrict_id || selectedDest.district_id || selectedDest.city_id || selectedDest.id || '';
      const res = await api.post(`/store/${subdomain}/shipping/calculate`, {
        tenant_subdomain: subdomain,
        destination: String(destId),
        weight: totalWeight,
        courier: selectedCourier,
        price: subtotal
      });
      setShippingCosts(res.data.costs || []);
      if (res.data.costs?.length > 0) {
        setSelectedShipping(res.data.costs[0]);
      }
      if (res.data.message) {
        toast.info(res.data.message);
      }
    } catch (e) {
      toast.error('Gagal menghitung ongkir');
    } finally {
      setShippingLoading(false);
    }
  };

  const subtotal = cart.reduce((a, c) => a + (c.price * c.quantity), 0);
  const bumpTotal = selectedBumps.reduce((a, b) => a + (b.bump_price ?? b.product?.price ?? 0), 0);
  // COD fee: use dynamic fee from Komship shipping calculation, fallback to tenant static fee
  const codFee = form.is_cod ? (selectedShipping?.cod_fee || codSettings?.cod_fee || 0) : 0;
  const shippingCost = (shippingInfo?.free_shipping_min > 0 && subtotal >= shippingInfo.free_shipping_min) ? 0 : (selectedShipping?.cost || 0);
  const totalWeight = cart.reduce((a, c) => a + ((c.weight || 500) * c.quantity), 0);
  const total = subtotal + bumpTotal + codFee + shippingCost - discount - loyaltyDiscount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) {
      toast.error('Nama dan email harus diisi');
      return;
    }
    if (cart.length === 0) {
      toast.error('Keranjang kosong');
      return;
    }
    setSubmitting(true);
    try {
      // Combine cart items with bump items
      const allItems = [
        ...cart.map(c => c.is_bundle
          ? { bundle_id: c.bundle_id, quantity: c.quantity }
          : { product_id: c.product_id, quantity: c.quantity, ...(c.is_high_ticket ? { ht_payment_type: c.ht_payment_type || 'booking' } : {}) }
        ),
        ...selectedBumps.map(b => ({ product_id: b.product_id, quantity: 1, is_bump: true }))
      ];

      const destId = selectedDest?.id || selectedDest?.subdistrict_id || selectedDest?.district_id || selectedDest?.city_id || '';
      const destLabel = selectedDest
        ? `${selectedDest.subdistrict_name || selectedDest.district_name || selectedDest.city_name || ''}, ${selectedDest.province_name || selectedDest.province || ''}`
        : '';

      const payload = {
        tenant_subdomain: subdomain,
        ...form,
        items: allItems,
        shipping_cost: shippingCost,
        shipping_courier: selectedShipping?.courier || '',
        shipping_service: selectedShipping?.service || '',
        destination_id: String(destId),
        destination_label: destLabel,
        cod_fee: codFee,
        loyalty_points_to_redeem: loyaltyPointsUsed,
        reseller_code: form.reseller_code,
        is_dropship: form.is_dropship,
        dropship_sender_name: form.dropship_sender_name,
        dropship_sender_phone: form.dropship_sender_phone,
      };
      const r = await api.post('/store/checkout', payload);
      trackEvent('Purchase', { value: total, num_items: cart.length, order_id: r.data?.order_id || r.data?.id });
      setOrderResult(r.data);
      setStep(3);
      // Fetch upsell offers based on purchased product IDs
      try {
        const orderItems = r.data?.order?.items || [];
        const purchasedIds = orderItems.map(i => i.product_id).filter(Boolean);
        const uniqueIds = [...new Set(purchasedIds)].join(',');
        if (uniqueIds) {
          const uRes = await api.get(`/store/${subdomain}/upsell-offers?product_ids=${uniqueIds}`);
          const offers = uRes.data.upsell_offers || [];
          setUpsellOffers(offers);
          if (offers.length > 0) {
            setCurrentFunnel(offers[0]);
            setUpsellState('showing_upsell');
          }
        }
      } catch (ue) { console.error('Upsell fetch error:', ue); }
      localStorage.removeItem(`cart_${subdomain}`);
      setCart([]);
      saveCartPersistent([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout gagal');
    } finally {
      setSubmitting(false);
    }
  };

  // Upsell/Downsell handlers
  const handleAcceptOffer = async (offerType) => {
    if (!currentFunnel || !orderResult?.order?.id) return;
    setUpsellLoading(true);
    try {
      const res = await api.post(`/store/${subdomain}/upsell-accept`, {
        original_order_id: orderResult.order.id,
        funnel_id: currentFunnel.id,
        offer_type: offerType,
        payment_method: orderResult.order.payment_method || 'qris',
      });
      setUpsellOrderResult(res.data);
      setUpsellState('done');
      if (res.data.payment_url) {
        window.open(res.data.payment_url, '_blank');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memproses penawaran');
    } finally {
      setUpsellLoading(false);
    }
  };

  const handleDeclineOffer = () => {
    if (upsellState === 'showing_upsell' && currentFunnel?.downsell_product_id && currentFunnel?.downsell_product) {
      setUpsellState('showing_downsell');
    } else {
      setUpsellState('done');
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const primaryColor = store?.primary_color || '#2D6CDF';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to={`/store/${subdomain}`} className="text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-bold text-lg text-slate-900">Checkout</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Steps */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-6 sm:mb-8 px-1">
          {['Keranjang', 'Pembayaran', 'Selesai'].map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 sm:gap-2">
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                step > i + 1 ? 'bg-emerald-500 text-white' :
                step === i + 1 ? 'text-white' : 'bg-slate-200 text-slate-500'
              }`} style={step === i + 1 ? { backgroundColor: primaryColor } : {}}>
                {step > i + 1 ? <CheckCircle size={16} /> : i + 1}
              </div>
              <span className={`text-xs sm:text-sm hidden sm:inline ${step === i + 1 ? 'font-medium text-slate-900' : 'text-slate-500'}`}>{s}</span>
              {i < 2 && <div className={`w-6 sm:w-12 h-0.5 ${step > i + 1 ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Cart */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShoppingBag size={18} style={{ color: primaryColor }} /> Keranjang ({cart.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {cart.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {cart.map((item, idx) => (
                        <div key={item.bundle_id || item.product_id} className="p-4">
                          <div className="flex gap-3">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                              {item.image ? (
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ShoppingBag size={20} className="text-slate-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-start gap-x-2 gap-y-1 pr-1">
                                <h3 className="font-medium text-slate-900 text-sm sm:text-base leading-snug">{item.name}</h3>
                                {item.is_bundle && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium shrink-0">Bundle</span>}
                              </div>
                              <p className="text-sm mt-0.5" style={{ color: primaryColor }}>Rp {fmt(item.price)}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 shrink-0 h-8 w-8 sm:hidden"
                              onClick={() => removeItem(idx)}
                              aria-label="Hapus item"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 sm:border-0 sm:pt-0 sm:mt-2">
                            <div className="flex items-center gap-1.5">
                              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateQuantity(idx, -1)}>
                                <Minus size={14} />
                              </Button>
                              <span className="w-8 text-center font-medium text-sm">{item.quantity}</span>
                              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateQuantity(idx, 1)}>
                                <Plus size={14} />
                              </Button>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="font-semibold text-sm sm:text-base whitespace-nowrap">Rp {fmt(item.price * item.quantity)}</p>
                              <Button variant="ghost" size="sm" className="text-red-500 hidden sm:inline-flex" onClick={() => removeItem(idx)}>
                                <Trash2 size={14} className="mr-1" /> Hapus
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <ShoppingBag size={48} className="mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">Keranjang kosong</p>
                      <Link to={`/store/${subdomain}`}>
                        <Button variant="link" style={{ color: primaryColor }}>Belanja Sekarang</Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Order Bumps */}
              {orderBumps.length > 0 && (
                <Card className="border-slate-200 shadow-sm border-dashed" data-testid="order-bumps-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Zap size={18} className="text-amber-500" /> Penawaran Spesial
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {orderBumps.map(bump => {
                      const isSelected = selectedBumps.find(b => b.id === bump.id);
                      const bumpPrice = bump.bump_price ?? bump.product?.price ?? 0;
                      return (
                        <div
                          key={bump.id}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:border-amber-200'}`}
                          onClick={() => toggleBump(bump)}
                          data-testid={`order-bump-${bump.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <input type="checkbox" checked={!!isSelected} readOnly className="h-5 w-5 accent-amber-500 mt-1 shrink-0" />
                            <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                              {(bump.image_url || bump.product?.images?.[0]) ? (
                                <img src={bump.image_url || bump.product?.images?.[0]} alt={bump.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package size={16} className="text-slate-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-amber-800 leading-snug">{bump.headline}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{bump.product?.name}</p>
                              {bump.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{bump.description}</p>}
                              <span className="inline-block mt-2 font-bold text-amber-600 sm:hidden">+Rp {fmt(bumpPrice)}</span>
                            </div>
                            <span className="font-bold text-amber-600 shrink-0 hidden sm:block">+Rp {fmt(bumpPrice)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Bundle Suggestions */}
              {bundleSuggestions.length > 0 && (
                <Card className="border-purple-200 shadow-sm bg-purple-50/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Package size={18} className="text-purple-500" /> Hemat dengan Bundle
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {bundleSuggestions.map(b => {
                      const savings = b.original_total && b.final_price ? Math.round((1 - b.final_price / b.original_total) * 100) : 0;
                      const alreadyInCart = cart.some(c => c.bundle_id === b.id);
                      return (
                        <div key={b.id} className="p-4 rounded-lg border border-purple-200 bg-white">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="w-14 h-14 bg-purple-50 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                                {b.images?.[0] ? (
                                  <img src={b.images[0]} alt={b.name} className="w-full h-full object-cover" />
                                ) : (
                                  <Package size={20} className="text-purple-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-slate-900 leading-snug">{b.name}</p>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                                  {b.original_total > b.final_price && (
                                    <span className="text-xs text-slate-400 line-through">Rp {fmt(b.original_total)}</span>
                                  )}
                                  <span className="text-sm font-bold text-purple-600">Rp {fmt(b.final_price)}</span>
                                  {savings > 0 && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">-{savings}%</span>}
                                </div>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              disabled={alreadyInCart}
                              onClick={() => addBundleToCart(b)}
                              className={`w-full sm:w-auto shrink-0 ${alreadyInCart ? 'bg-slate-200 text-slate-500' : 'bg-purple-600 hover:bg-purple-700 text-white'}`}
                            >
                              {alreadyInCart ? 'Sudah ditambah' : 'Tambah'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Voucher Code */}
              <Card className="border-slate-200 shadow-sm" data-testid="voucher-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Tag size={18} style={{ color: primaryColor }} /> Kode Voucher
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {voucherApplied ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div>
                        <span className="font-medium text-emerald-700">{voucherApplied.code}</span>
                        <span className="text-sm text-emerald-600 ml-2">
                          - Diskon Rp {fmt(discount)}
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={removeVoucher}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        value={voucherInput}
                        onChange={(e) => setVoucherInput(e.target.value)}
                        placeholder="Kode voucher"
                        className="uppercase flex-1 min-w-0"
                        data-testid="voucher-input"
                        onKeyDown={(e) => e.key === 'Enter' && validateVoucher()}
                      />
                      <Button
                        onClick={validateVoucher}
                        disabled={voucherLoading || !voucherInput.trim()}
                        className="w-full sm:w-auto shrink-0"
                        style={{ backgroundColor: primaryColor }}
                        data-testid="apply-voucher-btn"
                      >
                        {voucherLoading ? <Loader2 size={16} className="animate-spin" /> : 'Terapkan'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Loyalty Points */}
              {loyaltyInfo && loyaltyInfo.enabled && (
                <Card className="border-amber-200 shadow-sm" data-testid="loyalty-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Gift size={18} className="text-amber-500" /> Poin Loyalty
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-2">
                      Saldo poin Anda: <strong className="text-amber-600">{Number(loyaltyInfo.points).toLocaleString('id-ID')} poin</strong>
                    </p>
                    {loyaltyPointsUsed > 0 ? (
                      <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div>
                          <span className="font-medium text-amber-700">{loyaltyPointsUsed} poin</span>
                          <span className="text-sm text-amber-600 ml-2">- Diskon Rp {fmt(loyaltyDiscount)}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={removeLoyaltyPoints}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ) : loyaltyInfo.points >= (loyaltyInfo.settings?.min_redeem_points || 10) ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          type="number"
                          value={loyaltyPointsInput}
                          onChange={e => setLoyaltyPointsInput(e.target.value)}
                          placeholder={`Min. ${loyaltyInfo.settings?.min_redeem_points || 10} poin`}
                          min={loyaltyInfo.settings?.min_redeem_points || 10}
                          max={loyaltyInfo.points}
                          className="flex-1 min-w-0"
                          data-testid="loyalty-points-input"
                        />
                        <Button
                          onClick={applyLoyaltyPoints}
                          disabled={loyaltyLoading || !loyaltyPointsInput}
                          className="w-full sm:w-auto shrink-0 bg-amber-500 hover:bg-amber-600"
                          data-testid="apply-loyalty-btn"
                        >
                          {loyaltyLoading ? <Loader2 size={16} className="animate-spin" /> : 'Tukar'}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400">Poin belum mencukupi untuk ditukar (min. {loyaltyInfo.settings?.min_redeem_points || 10} poin)</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Summary Sidebar */}
            <div>
              <Card className="border-slate-200 shadow-sm sticky top-20">
                <CardHeader>
                  <CardTitle className="text-base">Ringkasan</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal</span>
                    <span>Rp {fmt(subtotal)}</span>
                  </div>
                  {bumpTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Order Bump</span>
                      <span>+Rp {fmt(bumpTotal)}</span>
                    </div>
                  )}
                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Diskon Voucher</span>
                      <span>-Rp {fmt(discount)}</span>
                    </div>
                  )}
                  {loyaltyDiscount > 0 && (
                    <div className="flex justify-between text-sm text-amber-600">
                      <span>Diskon Poin ({loyaltyPointsUsed} poin)</span>
                      <span>-Rp {fmt(loyaltyDiscount)}</span>
                    </div>
                  )}
                  {shippingCost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Ongkir</span>
                      <span>Rp {fmt(shippingCost)}</span>
                    </div>
                  )}
                  {shippingInfo?.free_shipping_min > 0 && subtotal < shippingInfo.free_shipping_min && (
                    <p className="text-xs text-amber-600">
                      Gratis ongkir untuk pembelian di atas Rp {fmt(shippingInfo.free_shipping_min)}
                    </p>
                  )}
                  {shippingInfo?.free_shipping_min > 0 && subtotal >= shippingInfo.free_shipping_min && (
                    <p className="text-xs text-emerald-600 font-medium">
                      &#10003; Gratis ongkir!
                    </p>
                  )}
                  <div className="border-t border-slate-100 pt-3 flex justify-between font-bold">
                    <span>Total</span>
                    <span style={{ color: primaryColor }}>Rp {fmt(total)}</span>
                  </div>
                  {/* High-ticket termin info */}
                  {cart.some(c => c.is_high_ticket) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <p className="font-medium text-blue-800 mb-1">&#128179; Produk High-Ticket (Termin)</p>
                      {cart.filter(c => c.is_high_ticket).map(c => (
                        <p key={c.product_id} className="text-blue-700 text-xs">
                          {c.name} — {c.ht_payment_type === 'booking' ? 'Booking Fee' : c.ht_payment_type === 'dp' ? 'Down Payment' : 'Bayar Lunas'}
                        </p>
                      ))}
                      <p className="text-xs text-blue-600 mt-1">Pembayaran pertama akan diproses, sisa cicilan akan dijadwalkan.</p>
                    </div>
                  )}
                  <Button
                    onClick={() => setStep(2)}
                    className="w-full"
                    style={{ backgroundColor: primaryColor }}
                    disabled={cart.length === 0}
                    data-testid="continue-to-payment-btn"
                  >
                    Lanjut ke Pembayaran
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Info */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Informasi Pembeli</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Nama Lengkap *</Label>
                      <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nama Anda" data-testid="customer-name-input" required />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} onBlur={fetchLoyaltyInfo} placeholder="email@example.com" data-testid="customer-email-input" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>No. Telepon</Label>
                      <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="08123456789" data-testid="customer-phone-input" />
                    </div>
                    <div>
                      <Label>Kode Referral</Label>
                      <Input value={form.affiliate_code} onChange={(e) => {
                        const code = e.target.value;
                        setForm({ ...form, affiliate_code: code });
                        setAffiliateValid(null);
                        setAffiliateName('');
                        if (code.trim()) {
                          api.get(`/store/${subdomain}/validate-affiliate`, { params: { code: code.trim() } })
                            .then(res => {
                              if (res.data.valid) { setAffiliateName(res.data.name); setAffiliateValid(true); }
                              else setAffiliateValid(false);
                            })
                            .catch(() => setAffiliateValid(false));
                        }
                      }} placeholder="Kode affiliate" />
                      {affiliateValid === true && affiliateName && (
                        <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Direferensikan oleh: <strong>{affiliateName}</strong>
                        </p>
                      )}
                      {affiliateValid === false && form.affiliate_code && (
                        <p className="text-sm text-red-500 mt-1">Kode affiliate tidak valid</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Alamat Lengkap</Label>
                    <Textarea value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} placeholder="Alamat lengkap untuk pengiriman" rows={2} data-testid="customer-address-input" />
                  </div>
                  <div>
                    <Label>Catatan (opsional)</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Catatan untuk penjual..." rows={2} />
                  </div>

                  {/* Reseller Code */}
                  <div>
                    <Label>Kode Reseller</Label>
                    <Input value={form.reseller_code} onChange={(e) => {
                      const code = e.target.value.toUpperCase();
                      setForm({ ...form, reseller_code: code });
                      setResellerPrices(null);
                      setResellerTierName('');
                      if (code.trim().length >= 4) {
                        api.get(`/store/${subdomain}/reseller-prices`, { headers: { 'X-Reseller-Code': code } })
                          .then(res => { setResellerPrices(res.data.prices); setResellerTierName(res.data.tier_name || ''); })
                          .catch(() => { setResellerPrices(null); });
                      }
                    }} placeholder="Kode reseller (opsional)" />
                    {resellerTierName && <p className="text-sm text-green-600 mt-1">Tier: <strong>{resellerTierName}</strong> — harga reseller akan diterapkan</p>}
                  </div>

                  {/* Dropship Toggle */}
                  <div className="border rounded-lg p-3 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.is_dropship} onChange={(e) => setForm({ ...form, is_dropship: e.target.checked })} className="rounded" />
                      <span className="font-medium text-sm">Kirim sebagai Dropship</span>
                    </label>
                    {form.is_dropship && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Nama Pengirim</Label>
                          <Input value={form.dropship_sender_name} onChange={(e) => setForm({ ...form, dropship_sender_name: e.target.value })} placeholder="Nama toko / reseller" />
                        </div>
                        <div>
                          <Label>No. HP Pengirim</Label>
                          <Input value={form.dropship_sender_phone} onChange={(e) => setForm({ ...form, dropship_sender_phone: e.target.value })} placeholder="08xxxxxxxxxx" />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Shipping Calculator */}
              {hasPhysicalItems && shippingInfo?.has_shipping && (
                <Card className="border-slate-200 shadow-sm" data-testid="shipping-calculator-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Truck size={18} style={{ color: primaryColor }} /> Kalkulasi Ongkir
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {shippingInfo.free_shipping_min > 0 && subtotal >= shippingInfo.free_shipping_min ? (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 font-medium">
                        &#10003; Gratis ongkir! Pembelian Anda sudah memenuhi minimum Rp {fmt(shippingInfo.free_shipping_min)}
                      </div>
                    ) : (
                      <>
                        {shippingInfo.flat_rate > 0 ? (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                            Ongkir flat: <strong>Rp {fmt(shippingInfo.flat_rate)}</strong>
                          </div>
                        ) : (shippingInfo.provider === 'rajaongkir' || shippingInfo.provider === 'komship') ? (
                          <>
                            {/* Destination Search */}
                            <div>
                              <Label>Cari Kota/Kecamatan Tujuan</Label>
                              <div className="flex gap-2">
                                <Input
                                  value={destSearch}
                                  onChange={(e) => setDestSearch(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchDestination())}
                                  placeholder="Ketik nama kota atau kecamatan..."
                                  data-testid="destination-search-input"
                                />
                                <Button type="button" variant="outline" onClick={searchDestination} disabled={destSearching}>
                                  {destSearching ? <Loader2 size={16} className="animate-spin" /> : 'Cari'}
                                </Button>
                              </div>
                            </div>
                            {destResults.length > 0 && (
                              <div className="border rounded-lg max-h-40 overflow-y-auto bg-white">
                                {destResults.map((item, i) => {
                                  const name = item.label || item.subdistrict_name || item.district_name || item.city_name || '';
                                  const detail = item.label ? '' : `— ${item.district_name || item.city_name || ''}, ${item.province_name || item.province || ''}`;
                                  return (
                                    <button key={i} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-0"
                                      onClick={() => { setSelectedDest(item); setDestResults([]); setDestSearch(''); }}>
                                      <span className="font-medium">{name}</span>
                                      {detail && <span className="text-slate-500 ml-1">{detail}</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {selectedDest && (
                              <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-2 rounded-lg">
                                <Truck size={14} />
                                <span>Tujuan: <strong>{selectedDest.label || `${selectedDest.subdistrict_name || selectedDest.district_name || selectedDest.city_name}, ${selectedDest.province_name || selectedDest.province}`}</strong></span>
                                <button type="button" className="ml-auto text-xs text-blue-500 hover:text-blue-700 underline" onClick={() => { setSelectedDest(null); setShippingCosts([]); setSelectedShipping(null); }}>Ganti</button>
                              </div>
                            )}

                            {/* Courier Selection */}
                            {selectedDest && shippingInfo.enabled_couriers?.length > 0 && (
                              <div>
                                <Label>Pilih Kurir</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {shippingInfo.enabled_couriers.map(c => (
                                    <button key={c} type="button"
                                      className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${selectedCourier === c ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                                      onClick={() => setSelectedCourier(c)}>
                                      {c.toUpperCase()}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedDest && selectedCourier && (
                              <Button type="button" variant="outline" onClick={calculateShipping} disabled={shippingLoading} data-testid="calc-shipping-btn">
                                {shippingLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Truck size={16} className="mr-2" />}
                                Hitung Ongkir
                              </Button>
                            )}

                            {shippingCosts.length > 0 && (
                              <RadioGroup value={selectedShipping?.service || ''} onValueChange={(v) => {
                                const found = shippingCosts.find(c => c.service === v);
                                setSelectedShipping(found);
                              }}>
                                <div className="space-y-2">
                                  {shippingCosts.map((cost, idx) => (
                                    <label key={idx} className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedShipping?.service === cost.service ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                                      <div className="flex items-center gap-3">
                                        <RadioGroupItem value={cost.service} />
                                        <div>
                                          <span className="font-medium text-sm">{cost.courier} - {cost.service}</span>
                                          <p className="text-xs text-slate-500">{cost.description} ({cost.etd})</p>
                                          {cost.cod_available && (
                                            <span className="text-xs text-emerald-600 font-medium">COD tersedia {cost.cod_fee > 0 ? `(fee Rp ${fmt(cost.cod_fee)})` : ''}</span>
                                          )}
                                        </div>
                                      </div>
                                      <span className="font-bold text-sm">Rp {fmt(cost.cost)}</span>
                                    </label>
                                  ))}
                                </div>
                              </RadioGroup>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-slate-500">Ongkir akan dihitung oleh penjual</p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Payment Method */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CreditCard size={18} style={{ color: primaryColor }} /> Metode Pembayaran
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={form.is_cod ? 'cod' : form.payment_method} onValueChange={(v) => {
                    if (v === 'cod') setForm({ ...form, is_cod: true });
                    else setForm({ ...form, is_cod: false, payment_method: v });
                  }}>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${!form.is_cod && form.payment_method === 'qris' ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <RadioGroupItem value="qris" />
                        <QrCode size={20} className="text-slate-600" />
                        <span className="font-medium">QRIS</span>
                      </label>
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${!form.is_cod && form.payment_method === 'va' ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <RadioGroupItem value="va" />
                        <Building size={20} className="text-slate-600" />
                        <span className="font-medium">Virtual Account</span>
                      </label>
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${!form.is_cod && form.payment_method === 'ewallet' ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <RadioGroupItem value="ewallet" />
                        <Wallet size={20} className="text-slate-600" />
                        <span className="font-medium">E-Wallet</span>
                      </label>
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${form.is_cod ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <RadioGroupItem value="cod" />
                        <Truck size={20} className="text-slate-600" />
                        <span className="font-medium">COD</span>
                      </label>
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${!form.is_cod && form.payment_method === 'bank_transfer' ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <RadioGroupItem value="bank_transfer" />
                        <Landmark size={20} className="text-slate-600" />
                        <span className="font-medium">Transfer Bank</span>
                      </label>
                    </div>
                  </RadioGroup>
                  {form.is_cod && codFee > 0 && (
                    <p className="text-sm text-amber-600 mt-3">Biaya COD: Rp {fmt(codFee)}</p>
                  )}
                  {form.is_cod && selectedShipping && selectedShipping.cod_available === false && (
                    <p className="text-sm text-red-500 mt-3">⚠ Kurir {selectedShipping.courier} {selectedShipping.service} tidak mendukung COD. Pilih kurir lain atau metode pembayaran lain.</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Summary */}
            <div>
              <Card className="border-slate-200 shadow-sm sticky top-20">
                <CardHeader>
                  <CardTitle className="text-base">Ringkasan Pesanan</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {cart.map(item => (
                    <div key={item.product_id} className="flex justify-between gap-3 text-sm">
                      <span className="text-slate-600 min-w-0">{item.name} <span className="text-slate-400">x{item.quantity}</span></span>
                      <span className="shrink-0 whitespace-nowrap">Rp {fmt(item.price * item.quantity)}</span>
                    </div>
                  ))}
                  {selectedBumps.map(bump => (
                    <div key={bump.id} className="flex justify-between text-sm text-amber-700">
                      <span className="truncate">{bump.product?.name} (bump)</span>
                      <span>Rp {fmt(bump.bump_price ?? bump.product?.price ?? 0)}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-100 pt-3 flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal</span>
                    <span>Rp {fmt(subtotal + bumpTotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Diskon ({voucherApplied?.code})</span>
                      <span>-Rp {fmt(discount)}</span>
                    </div>
                  )}
                  {shippingCost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Ongkir ({selectedShipping?.courier} {selectedShipping?.service})</span>
                      <span>Rp {fmt(shippingCost)}</span>
                    </div>
                  )}
                  {form.is_cod && codFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Biaya COD</span>
                      <span>Rp {fmt(codFee)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-100 pt-3 flex justify-between font-bold">
                    <span>Total</span>
                    <span style={{ color: primaryColor }}>Rp {fmt(total)}</span>
                  </div>
                  <Button type="submit" className="w-full" style={{ backgroundColor: primaryColor }} disabled={submitting} data-testid="place-order-btn">
                    {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <CreditCard size={16} className="mr-2" />}
                    {submitting ? 'Memproses...' : 'Bayar Sekarang'}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => setStep(1)}>
                    Kembali ke Keranjang
                  </Button>
                </CardContent>
              </Card>
            </div>
          </form>
        )}

        {/* Step 3: Success */}
        {step === 3 && orderResult && (() => {
          const o = orderResult.order || {};
          const items = o.items || [];
          const paymentMethodLabels = { qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet', cod: 'COD', bank_transfer: 'Transfer Bank' };
          const payMethod = o.is_cod ? 'COD' : (paymentMethodLabels[o.payment_method] || o.payment_method || '-');
          return (
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Success Banner */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="py-8 text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={40} className="text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">Pesanan Berhasil!</h2>
                <p className="text-slate-500">Terima kasih atas pesanan Anda</p>
              </CardContent>
            </Card>

            {/* Upsell/Downsell Offer */}
            {currentFunnel && upsellState !== 'idle' && upsellState !== 'done' && (
              <Card className="border-2 shadow-lg animate-in fade-in slide-in-from-bottom-4" style={{ borderColor: primaryColor }}>
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
                      <TrendingUp size={16} />
                      {upsellState === 'showing_upsell' ? 'Penawaran Spesial!' : 'Penawaran Terakhir!'}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {upsellState === 'showing_upsell' ? currentFunnel.upsell_headline : currentFunnel.downsell_headline}
                    </h3>
                  </div>

                  {(() => {
                    const isUpsell = upsellState === 'showing_upsell';
                    const product = isUpsell ? currentFunnel.upsell_product : currentFunnel.downsell_product;
                    const specialPrice = isUpsell ? currentFunnel.upsell_price : currentFunnel.downsell_price;
                    const description = isUpsell ? currentFunnel.upsell_description : currentFunnel.downsell_description;
                    const imageUrl = isUpsell ? currentFunnel.upsell_image_url : currentFunnel.downsell_image_url;
                    if (!product) return null;
                    const originalPrice = product.sale_price || product.price;
                    const finalPrice = specialPrice != null ? specialPrice : originalPrice;
                    return (
                      <div className="flex flex-col sm:flex-row gap-4 items-center">
                        {(imageUrl || product.images?.[0]) && (
                          <img
                            src={imageUrl || product.images[0]}
                            alt={product.name}
                            className="w-32 h-32 object-cover rounded-lg border"
                          />
                        )}
                        <div className="flex-1 text-center sm:text-left">
                          <p className="font-semibold text-lg text-slate-900">{product.name}</p>
                          {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
                          <div className="mt-2 flex items-center gap-2 justify-center sm:justify-start">
                            <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                              Rp {fmt(finalPrice)}
                            </span>
                            {specialPrice != null && specialPrice < originalPrice && (
                              <span className="text-sm text-slate-400 line-through">Rp {fmt(originalPrice)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="mt-6 flex flex-col gap-2">
                    <Button
                      size="lg"
                      className="w-full text-base font-semibold"
                      style={{ backgroundColor: primaryColor }}
                      disabled={upsellLoading}
                      onClick={() => handleAcceptOffer(upsellState === 'showing_upsell' ? 'upsell' : 'downsell')}
                    >
                      {upsellLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : <TrendingUp size={18} className="mr-2" />}
                      Ya, Tambahkan ke Pesanan!
                    </Button>
                    <button
                      className="text-sm text-slate-400 hover:text-slate-600 py-2 flex items-center justify-center gap-1"
                      onClick={handleDeclineOffer}
                      disabled={upsellLoading}
                    >
                      <X size={14} /> Tidak, Terima Kasih
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upsell Accepted Confirmation */}
            {upsellOrderResult && upsellState === 'done' && (
              <Card className="border-emerald-200 shadow-sm bg-emerald-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={24} className="text-emerald-500" />
                    <div>
                      <p className="font-medium text-emerald-800">Pesanan tambahan berhasil dibuat!</p>
                      <p className="text-sm text-emerald-600">Order #{upsellOrderResult.order?.order_number}</p>
                    </div>
                  </div>
                  {upsellOrderResult.payment_url && (
                    <a href={upsellOrderResult.payment_url} target="_blank" rel="noreferrer" className="block mt-3">
                      <Button size="sm" className="w-full" style={{ backgroundColor: primaryColor }}>
                        <CreditCard size={14} className="mr-2" /> Bayar Pesanan Tambahan — Rp {fmt(upsellOrderResult.order?.total)}
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Order Detail */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Detail Pesanan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Nomor Pesanan</p>
                    <p className="font-mono font-bold" style={{ color: primaryColor }}>{o.order_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Nomor Invoice</p>
                    <p className="font-mono font-bold">{orderResult.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Tanggal</p>
                    <p className="font-medium">{new Date(o.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${o.is_cod ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                      {o.is_cod ? 'Menunggu Konfirmasi COD' : 'Menunggu Pembayaran'}
                    </span>
                  </div>
                </div>

                {/* Items */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Produk yang Dibeli</p>
                  <div className="space-y-2">
                    {items.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm py-1">
                        <span className="text-slate-600">{item.product_name} <span className="text-slate-400">x{item.quantity}</span></span>
                        <span className="font-medium">Rp {fmt(item.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price Summary */}
                <div className="border-t pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subtotal</span>
                    <span>Rp {fmt(o.subtotal)}</span>
                  </div>
                  {o.discount > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Diskon {o.voucher_code ? `(${o.voucher_code})` : ''}</span>
                      <span>-Rp {fmt(o.discount)}</span>
                    </div>
                  )}
                  {o.shipping_cost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ongkir {o.shipping_courier ? `(${o.shipping_courier} ${o.shipping_service || ''})` : ''}</span>
                      <span>Rp {fmt(o.shipping_cost)}</span>
                    </div>
                  )}
                  {o.cod_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Biaya COD</span>
                      <span>Rp {fmt(o.cod_fee)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between font-bold text-base">
                    <span>Total Pembayaran</span>
                    <span style={{ color: primaryColor }}>Rp {fmt(o.total)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Instructions */}
            <Card className="border-slate-200 shadow-sm border-l-4" style={{ borderLeftColor: o.is_cod ? '#f59e0b' : primaryColor }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  {o.is_cod ? <Truck size={18} className="text-amber-500" /> : <CreditCard size={18} style={{ color: primaryColor }} />}
                  Instruksi Pembayaran
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Metode Pembayaran</span>
                  <span className="font-medium">{payMethod}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Jumlah yang Harus Dibayar</span>
                  <span className="text-lg font-bold" style={{ color: primaryColor }}>Rp {fmt(o.total)}</span>
                </div>

                {o.is_cod ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-amber-800">&#128230; Bayar di Tempat (COD)</p>
                    <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                      <li>Pesanan Anda akan diproses dan dikirim oleh penjual</li>
                      <li>Siapkan uang tunai sebesar <strong>Rp {fmt(o.total)}</strong> saat barang tiba</li>
                      <li>Periksa barang sebelum melakukan pembayaran ke kurir</li>
                      <li>Kurir akan mengirim OTP untuk konfirmasi penerimaan</li>
                    </ul>
                  </div>
                ) : orderResult.payment_provider === 'bank_transfer' ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-blue-800 mb-3">&#127974; Transfer ke salah satu rekening berikut:</p>
                      <div className="space-y-3">
                        {(orderResult.bank_accounts || []).map((acc, i) => (
                          <div key={i} className="bg-white rounded-lg p-3 border border-blue-100">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-blue-900">{acc.bank_name}</span>
                              <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Nomor rekening disalin'); }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Copy size={12} /> Salin</button>
                            </div>
                            <p className="font-mono text-lg tracking-wider mt-1">{acc.account_number}</p>
                            <p className="text-sm text-slate-500">a.n. {acc.account_holder}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                        <strong>Transfer tepat:</strong> Rp {fmt(o.total)} agar verifikasi lebih cepat
                      </div>
                    </div>

                    {!proofSubmitted ? (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm font-medium text-slate-700">&#128206; Konfirmasi Pembayaran</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-xs">Bank Pengirim</Label><Input placeholder="BCA, BNI, BRI..." value={proofForm.bank_name} onChange={e => setProofForm({...proofForm, bank_name: e.target.value})} /></div>
                          <div><Label className="text-xs">Nama Pengirim</Label><Input placeholder="Nama di rekening" value={proofForm.account_name} onChange={e => setProofForm({...proofForm, account_name: e.target.value})} /></div>
                          <div><Label className="text-xs">Jumlah Transfer</Label><Input type="number" placeholder={String(o.total)} value={proofForm.amount} onChange={e => setProofForm({...proofForm, amount: e.target.value})} /></div>
                          <div><Label className="text-xs">Tanggal Transfer</Label><Input type="date" value={proofForm.transfer_date} onChange={e => setProofForm({...proofForm, transfer_date: e.target.value})} /></div>
                        </div>
                        <div><Label className="text-xs">URL Bukti Transfer (opsional)</Label><Input placeholder="https://... link gambar bukti" value={proofForm.proof_url} onChange={e => setProofForm({...proofForm, proof_url: e.target.value})} /></div>
                        <div><Label className="text-xs">Catatan (opsional)</Label><Input placeholder="Catatan tambahan" value={proofForm.notes} onChange={e => setProofForm({...proofForm, notes: e.target.value})} /></div>
                        <Button className="w-full" style={{ backgroundColor: primaryColor }} disabled={proofSubmitting || !proofForm.bank_name || !proofForm.account_name || !proofForm.amount} onClick={async () => {
                          setProofSubmitting(true);
                          try {
                            await api.post(`/store/${subdomain}/payment-confirmation`, { order_number: o.order_number, customer_email: o.customer_email, bank_name: proofForm.bank_name, account_name: proofForm.account_name, amount: parseInt(proofForm.amount), transfer_date: proofForm.transfer_date, proof_url: proofForm.proof_url, notes: proofForm.notes });
                            setProofSubmitted(true); toast.success('Konfirmasi pembayaran terkirim!');
                          } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim konfirmasi'); }
                          finally { setProofSubmitting(false); }
                        }}>
                          {proofSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                          Kirim Konfirmasi Pembayaran
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                        <CheckCircle size={32} className="text-emerald-500 mx-auto mb-2" />
                        <p className="font-medium text-emerald-800">Konfirmasi Terkirim!</p>
                        <p className="text-sm text-emerald-600 mt-1">Penjual akan memverifikasi pembayaran Anda</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orderResult.payment_url && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm font-medium text-blue-800">&#128179; Lakukan pembayaran melalui link berikut:</p>
                        <a href={orderResult.payment_url} target="_blank" rel="noreferrer" className="block">
                          <Button className="w-full" style={{ backgroundColor: primaryColor }} size="lg" data-testid="pay-now-btn">
                            <CreditCard size={18} className="mr-2" /> Bayar Sekarang — Rp {fmt(o.total)}
                          </Button>
                        </a>
                        <div className="text-xs text-blue-600 space-y-1">
                          <p>&#9200; Selesaikan pembayaran dalam <strong>24 jam</strong> agar pesanan tidak dibatalkan</p>
                          <p>&#128274; Pembayaran diproses secara aman melalui payment gateway</p>
                        </div>
                      </div>
                    )}
                    {!orderResult.payment_url && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Hubungi penjual untuk informasi pembayaran lebih lanjut.</p>
                      </div>
                    )}
                    <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 space-y-1">
                      <p className="font-medium text-slate-700">Langkah Pembayaran:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Klik tombol <strong>"Bayar Sekarang"</strong> di atas</li>
                        <li>Pilih metode pembayaran yang tersedia ({payMethod})</li>
                        <li>Ikuti instruksi pembayaran dari halaman payment gateway</li>
                        <li>Setelah pembayaran berhasil, status pesanan akan otomatis terupdate</li>
                        <li>Anda akan menerima notifikasi konfirmasi pembayaran</li>
                      </ol>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* High-Ticket Payment Schedule */}
            {orderResult.is_high_ticket && orderResult.payment_schedules?.length > 0 && (
              <Card className="border-blue-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard size={18} className="text-blue-600" />
                    Jadwal Pembayaran Termin
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {orderResult.payment_schedules.map((s, i) => (
                      <div key={s.id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                        <div>
                          <p className="font-medium text-slate-900">
                            {s.type === 'booking' ? 'Booking Fee' : s.type === 'dp' ? 'Down Payment' : `Cicilan ${s.installment_number || i}`}
                          </p>
                          <p className="text-xs text-slate-500">
                            Jatuh tempo: {new Date(s.due_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                          {s.product_name && <p className="text-xs text-slate-400">{s.product_name}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-bold" style={{ color: primaryColor }}>Rp {fmt(s.amount)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {s.status === 'paid' ? 'Lunas' : 'Belum Bayar'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-600 mt-3">Anda akan diingatkan sebelum jatuh tempo setiap cicilan. Cek jadwal di halaman member area.</p>
                </CardContent>
              </Card>
            )}

            {/* Customer Info Recap */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Informasi Pembeli</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Nama</p>
                    <p className="font-medium">{o.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{o.customer_email}</p>
                  </div>
                  {o.destination_label && (
                    <div className="col-span-2">
                      <p className="text-slate-500">Tujuan Pengiriman</p>
                      <p className="font-medium">{o.destination_label}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {orderResult.payment_url && !o.is_cod && (
                <a href={orderResult.payment_url} target="_blank" rel="noreferrer">
                  <Button style={{ backgroundColor: primaryColor }} size="lg">
                    <CreditCard size={16} className="mr-2" /> Bayar Sekarang
                  </Button>
                </a>
              )}
              <Link to={`/store/${subdomain}/thank-you?order=${o.order_number}&type=order`}>
                <Button variant="outline" size="lg" style={{ borderColor: primaryColor, color: primaryColor }}>
                  <FileText size={16} className="mr-2" /> Lihat Halaman Pesanan
                </Button>
              </Link>
              <Link to={`/store/${subdomain}`}>
                <Button variant="outline" size="lg" data-testid="continue-shopping-btn">
                  <ShoppingBag size={16} className="mr-2" /> Lanjut Belanja
                </Button>
              </Link>
            </div>
          </div>
          );
        })()}
      </main>
    </div>
  );
}
